<template>
<section>
<!-- Transition: Tab <-> Tab -->
<transition
    :name="updateTransition"
    mode="out-in"
>
    <div class="page-container transition-up py-5" id="page" :key="`active-segment-${segment}`">

        <div class="row align-items-center mb-3 transition-up-1">
            <div class="col-12 col-lg-7 ">
                <h1 id="page-title" class="h3 h2-md m-0">
                    <!-- {{ $t('community.h1') }} -->
                    {{ segment == 'specialists' ? $t('community.specialists') : $t('community.organizations') }}
                </h1>
            </div>
            <div class="col-12 col-lg-5">
                <Search
                    v-model="search"
                    name="searchservice"
                    :placeholder="$t('community.search')"
                    class="mt-3 mt-lg-0 flex-grow-1 search"
                    @input="debounceFilter"
                    @change="debounceFilter"
                    ref="search"
                    size="2"
                />
            </div>
        </div>    

        <!-- This is segment tab -->
        <div  class="transition-up-3">

            <!-- Captions -->
            <div
                class="item-captions bg-color-content mx-n3 my-1 px-3 py-1 d-none d-md-flex fw-700 color-gray-2 f-small"
            >
                <div class="item-caption-basic">
                    {{ segment == 'specialists' ? $t('community.listHead.specialist') : $t('community.listHead.company') }}
                </div>
                <div class="item-caption-specialization">{{ $t('community.listHead.specialization') }}</div>
                <div class="item-caption-works">{{ $t('community.listHead.latestWorks') }}</div>
                <div class="item-caption-actions">{{ $t('community.listHead.actions') }}</div>
            </div>

            <!-- Transition:  NoResult <-> List -->
            <transition name="fade-slide-top" mode="out-in">

                <!-- Transition: add/remove/update list-->
                <transition-group
                    v-if="itemsLength"
                    :name="loadTransition"
                    class="item-list mx-md-n2 mt-2"
                    tag="div"
                >
                    <!-- List -->
                    <template v-if="segment == 'specialists'">
                        <Card
                            v-for="user in items"
                            :key="`community-user-${user.id}`"
                            class="item-card mt-2 p-2"
                        >
                            <nuxt-link
                                :to="localePath(`/${user.slug || user.id}`)"
                                class="item-basic i-link"
                            >
                                <div class="item-icon user mr-2">
                                    <img class="block-image profile-shadow" :src="user.photo" :alt="user.displayName" width="48" height="48" loading="lazy">
                                </div>
                                <div class="item-info">
                                    <div class="fw-700">{{ user.displayName }}</div>
                                    <div v-if="user.country" class="mt-h f-small color-gray-2">{{ user.country }}</div>
                                    <div v-if="user.employees && user.employees.length > 0" class="mt-h f-small color-gray-2">
                                        <template v-if="user.employees[0].position">
                                            {{ $t('community.positionAtCompany', {
                                                position: user.employees[0].position,
                                                company: user.employees[0].company.name
                                            }) }}
                                        </template>
                                        <template v-else>
                                            {{ user.employees[0].company.name }}
                                        </template>
                                    </div>
                                </div>
                            </nuxt-link>

                            <div class="item-specialization">
                                <div v-if="user.specs" class="item-tags">
                                    <span
                                        v-for="(spec, i) in user.specs"
                                        :key="`spec-${user.id}-${i}`"
                                        class="tag gray"
                                    >{{ spec.name }}</span>

                                    <span v-if="user.specsLength > 2" class="tag gray">...</span>
                                </div>
                            </div>

                            <div class="item-works">
                                <nuxt-link
                                    v-for="work in user.works"
                                    :key="`work-${user.id}-${work.id}`"
                                    :to="localePath(`/case/${work.slug || work.id}`)"
                                    class="profile-shadow item-work elevate"
                                    :title="work.title"
                                >
                                    <img
                                        loading="lazy"
                                        :src="`${$CDN}/image/small/${work.thumbnail.name}`"
                                        :alt="work.title"
                                        class="obj-fit-image"
                                    >
                                </nuxt-link>
                            </div>

                            <div class="item-actions">
                                <Follow
                                    class="item-follow-button btn-secondary fw-600 tt-upper elevate"
                                    modelName="user"
                                    :modelId="user.id"
                                    :toggleClass="{
                                        follow: '',
                                        following: 'bg-color-rivulet-label color-content-bg following'
                                    }"
                                >
                                    <template v-slot:follow>
                                        <span class="icon-ic-user-add fw-normal color-gray-3 d-none d-md-block" />
                                        <span class="icon-ic-user-add-outline fw-normal d-md-none" />
                                        <span class="ml-1 ml-lg-h d-none d-md-block">{{ $t('general.follow') }}</span>
                                    </template>

                                    <template v-slot:following>
                                        <span class="icon-ic-tick-thin fw-normal color-gray-3 color-inherit"/>
                                        <span class="ml-1 ml-lg-h d-none d-md-block">{{ $t('general.following') }}</span>
                                    </template>
                                </Follow>

                                <nuxt-link
                                    :to="localePath(`/${user.slug || user.id}`)"
                                    class="item-link-button btn-secondary fw-600 tt-upper elevate"
                                >
                                    <span class="icon-ic-arrow-simple"></span>
                                </nuxt-link>
                            </div>
                        </Card>
                    </template>

                    <!-- List -->
                    <template v-else>
                        <Card
                            v-for="company in items"
                            :key="`community-company-${company.id}`"
                            class="item-card mt-2 p-2"
                        >
                            <nuxt-link
                                :to="localePath(`/${company.slug || company.id}`)"
                                class="item-basic i-link"
                            >
                                <div class="item-icon company mr-2">
                                    <img class="block-image profile-shadow" :src="company.logo" :alt="company.name" width="48" height="48" loading="lazy">
                                </div>
                                <div class="item-info">
                                    <div class="fw-700">{{ company.name }}</div>
                                    <div v-if="company.country" class="mt-h f-small color-gray-2">{{ company.country }}</div>
                                </div>
                            </nuxt-link>

                            <div class="item-specialization">
                                <div v-if="company.specs" class="item-tags">
                                    <span
                                        v-for="(spec, i) in company.specs"
                                        :key="`spec-${company.id}-${i}`"
                                        class="tag gray"
                                    >{{ spec.name }}</span>

                                    <span v-if="company.specsLength > 2" class="tag gray">...</span>
                                </div>
                            </div>

                            <div class="item-works">
                                <nuxt-link
                                    v-for="work in company.works"
                                    :key="`work-${company.id}-${work.id}`"
                                    :to="localePath(`/case/${work.slug || work.id}`)"
                                    class="profile-shadow item-work elevate"
                                    :title="work.title"
                                >
                                    <img
                                        loading="lazy"
                                        :src="`${$CDN}/image/small/${work.thumbnail.name}`"
                                        :alt="work.title"
                                        class="obj-fit-image"
                                    >
                                </nuxt-link>
                            </div>

                            <div class="item-actions">
                                <Follow
                                    class="item-follow-button btn-secondary fw-600 tt-upper elevate"
                                    modelName="company"
                                    :modelId="company.id"
                                    :toggleClass="{
                                        follow: '',
                                        following: 'bg-color-rivulet-label color-content-bg following'
                                    }"
                                >
                                    <template v-slot:follow>
                                        <span class="icon-ic-user-add fw-normal color-gray-3 d-none d-md-block" />
                                        <span class="icon-ic-user-add-outline fw-normal d-md-none" />
                                        <span class="ml-1 ml-lg-h d-none d-md-block">{{ $t('general.follow') }}</span>
                                    </template>

                                    <template v-slot:following>
                                        <span class="icon-ic-tick-thin fw-normal color-gray-3 color-inherit"/>
                                        <span class="ml-1 ml-lg-h d-none d-md-block">{{ $t('general.following') }}</span>
                                    </template>
                                </Follow>

                                <nuxt-link
                                    :to="localePath(`/${company.slug || company.id}`)"
                                    class="item-link-button btn-secondary fw-600 tt-upper elevate"
                                >
                                    <span class="icon-ic-arrow-simple"></span>
                                </nuxt-link>
                            </div>
                        </Card>
                    </template>
                </transition-group>

                <!-- No result -->
                <Card
                    v-else
                    :lower="true"
                    class="mt-3"
                >
                    <p class="color-text">{{ $t('component.ListView.notFoundTitle') }}</p>

                    <p class="f-small color-muted my-1">{{ $t('component.ListView.tips') }}</p>
                    <ul class="f-small color-muted mt-h list-disk">
                        <li>{{ $t('component.ListView.tip1') }}</li>
                        <li>{{ $t('component.ListView.tip2') }}</li>
                        <li>{{ $t('component.ListView.tip3') }}</li>
                    </ul>
                </Card>
            </transition>

            <!-- Pagination -->
            <Pagination
                class="mt-3"
                :page="page"
                :limit="limit"
                :visibleLength="$mq == 'xs' ? 3 : 5"
                :length="itemsLength"
                :loading="paginationLoading"
                :busy="paginationBusy"
                @change="goToPage"
                @load="loadPage"
            />
        </div>
        
    </div>

    <div class="sticky-panel d-flex justify-content-start ml-md-2">
        <button
            v-scroll-to="'.content-container'"
            :class="{'hidden': introVisible}"
            class="course-scroll-top-button btn-primary d-none d-md-flex"
        >
            <span class="course-scroll-top-icon icon-ic-arrow-down" />
        </button>
    </div>
</transition>
</section>
</template>


<script>
    import Card       from '~/components/cards/Card.vue';
    import DdButton   from '~/components/DdButton.vue';
    import Follow     from '~/components/profile/Follow.vue';
    import Pagination from '~/components/Pagination.vue';
    import Search     from '~/components/form/SearchInputLow.vue';


    const limit = 12;

import debounce from '~/utilities/debounce.js';

    export default {
        components: {
            Card,
            DdButton,
            Follow,
            Pagination,
            Search
        },

        name: 'Community',

        head () {
            const i18nHead = this.$nuxtI18nHead({ addSeoAttributes: true });

            return {
                title: this.$t('community.title'),

                meta: [
                    { hid: 'title',       name: "title",       content: this.$t('community.title') },
                    { hid: 'description', name: 'description', content: this.$t('community.description') },

                    { hid: "og:url",         property: "og:url",         content: `${process.env.baseUrl}/community`},
                    { hid: "og:title",       property: "og:title",       content: this.$t('community.title')},
                    { hid: "og:description", property: "og:description", content: this.$t('community.description')},
                    { hid: "og:image",       property: "og:image",       content: `${process.env.baseUrl}/images/CASES-OG.png`},

                    { hid: "twitter:url",         name: "twitter:url"  ,       content: `${process.env.baseUrl}/community`},
                    { hid: "twitter:title",       name: "twitter:title",       content: this.$t('community.title')},
                    { hid: "twitter:description", name: "twitter:description", content: this.$t('community.description')},
                    { hid: "twitter:image",       name: "twitter:image",       content: `${process.env.baseUrl}/images/CASES-OG.png`}
                ],

                link: [
                    ...i18nHead.link
                ]
            };
        },

        data () {
            return {
                search: this.$route.query.search,

                introVisible: true,

                items: [],
                itemsLength: 0,

                page: 1,
                limit,
                paginationLoading: false,
                paginationBusy: false,

                updatedAt: new Date().getTime(),

                segment: 'organizations',

                routeQueryCache: null,
            };
        },

        async asyncData({$axios, route, error, $appStore}) {
            $appStore.$patch({activeApp: 'community'});

            let params = {
                limit
            };

            let segment = 'organizations';
            let page = parseInt(route.query.page);

            if (route.query.segment && route.query.segment.toLowerCase() == 'specialists') {
                segment = 'specialists';
            }

            if (page >= 2) {
                params.skip = (page - 1) * limit;
            }

            if (route.query.search) params.search = route.query.search;

            let url = `${$axios.defaults.baseURL}/company/list`;

            if (segment == 'specialists') {
                url = `${$axios.defaults.baseURL}/user/list`;
            }

            let postData = await $axios.$get(url, {
                params
            })
            .catch((e) => {
                error({ statusCode: 404, message: '' });
            });

            let result = {
                segment,
                items: postData.items,
                itemsLength: postData.length
            }

            if (page) result.page = page;

            return result;
        },

        computed: {
            locale () {
                return this.$i18n.locale;
            },

            query () {
                let query = {
                    limit,
                    skip: (this.page -1) * this.limit
                };

                if (this.search) query.search = this.search;
                // if (this.segment == 'organizations') query.organizations = true;
                // if (this.segment == 'specialists') query.specialists = 1;

                return query;
            },

            updateTransition () {
                return this.paginationLoading ? 'none' : 'page'
            },

            loadTransition () {
                return this.paginationLoading ? 'none' : 'page'
            },
        },

        watch: {
            page (newVal) {
                this.newRouteQueryCache();

                if (newVal <= 1 && this.routeQueryCache) {
                    delete this.routeQueryCache.page;
                } else {
                    this.routeQueryCache.page = newVal;
                }

                this.debounceUrl();
            },

            search (newVal) {
                this.newRouteQueryCache();

                if (newVal === '' && this.routeQueryCache.search) {
                    delete this.routeQueryCache.search;
                } else {
                    this.routeQueryCache.search = newVal;
                }

                this.debounceUrl();
            },

            segment (newVal) {
                this.newRouteQueryCache();

                if (newVal == 'organizations' && this.routeQueryCache.segment) {
                    delete this.routeQueryCache.segment;
                } else {
                    this.routeQueryCache.segment = newVal;
                }

                this.debounceUrl();
            },

            '$route.query.search': function (value) {
                this.search = value;
            },

            '$route.query.segment': function (value) {
                if (value == this.segment) return;

                this.changeSegment(value == 'specialists' ? 'specialists' : 'organizations')
                // console.log(this.segment);
            }
        },

        mounted () {
            if (process.browser) {
                window.addEventListener('keydown', this.shortCuts);
                this.observe();
            }
        },

        methods: {
            newRouteQueryCache () {
                if (!this.routeQueryCache) {
                    this.routeQueryCache = Object.assign({}, this.$route.query);
                }
            },

            debounceUrl: debounce(function () {
                this.updateUrl();
            }, 100),

            updateUrl () {
                if (Object.keys(this.routeQueryCache).length === 0) {
                    this.$router.replace({query: null});
                } else {
                    this.$router.replace({query: this.routeQueryCache});
                }

                this.routeQueryCache = null;
            },

            async changeSegment (name) {
                await this.applyFilter(name);

                this.segment = name;
            },

            cropContent () {
                this.$cropContent()
            },

            clearFilter () {
                this.search = '';

                this.applyFilter();
            },

            debounceFilter: debounce(function () {
                this.applyFilter();
            }, 300),

            async goToPage (page) {
                this.page = page;
                let data = await this.load(this.segment);

                let done = () => {
                    this.updatedAt = new Date().getTime();

                    this.items = data.items;
                    this.itemsLength = data.length;
                }

                this.$scrollTo(`#page`, {
                    offset: -80,
                    onDone: () => {done()},
                    onCancel: () => {done()}
                });
            },

            async loadPage () {
                this.paginationLoading = true;
                this.page++;
                let data = await this.load(this.segment);

                this.items.push(...data.items);
                this.itemsLength = data.length;
                this.paginationLoading = false;
            },

            async load (segment) {
                this.paginationBusy = true;

                let url = `/company/list`;

                if (segment == 'specialists') {
                    url = `/user/list`;
                }

                let data = await this.$axios.$get(url, {
                    params: this.query
                });

                this.paginationBusy = false;

                return data;
            },

            async applyFilter (segment) {
                this.page = 1;

                let seg = segment || this.segment;

                let data = await this.load(seg);

                this.updatedAt = new Date().getTime();

                this.items = data.items;
                this.itemsLength = data.length;
            },

            shortCuts (e) {
                let inputs = ['INPUT', 'TEXTAREA'];
                if (inputs.indexOf(e.target.tagName.toUpperCase()) >= 0) return true;

                // focus search
                if (e.keyCode === 191) {
                    e.preventDefault();
                    this.$refs.search.focus();
                }
            },

            focus (name) {

                setTimeout(() => {
                    this.$refs[name].focus();
                }, 150)
            },

            observe () {
                // Subscription observer
                const options = this.options || {
                    rootMargin: '0px',
                    threshold: [0, 0.03, 0.1, 0.25, 0.5, 0.75, 1.0]
                };

                // Scroll to top observer
                const scrollTopThreshold = 0.3;

                this.introObserver = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        const visible = entry.isIntersecting && entry.intersectionRatio >= scrollTopThreshold;
                        this.introVisible = visible;
                    })
                }, {
                    root:       null,
                    rootMargin: '0px',
                    threshold:  scrollTopThreshold
                });

                const introBlock = document.querySelector('#page-title');
                if (introBlock) this.introObserver.observe(introBlock);
            },

            unobserve: function () {
                if (this.introObserver)   this.introObserver.disconnect();
            }
        },

        beforeDestroy () {
            window.removeEventListener('keydown', this.shortCuts);
            this.unobserve();
        },
    }
</script>


<style lang="postcss" scoped>
    .tab-item.active > .color-gray-3 {
        color: inherit;
    }

    .order-wrapper {
        display:   flex;
        flex-flow: row wrap;

        @media screen and (min-width: 992px) {
            flex-wrap:   nowrap;
            align-items: center;
        }
    }

    .order-image,
    .order-text,
    .order-action {
        flex: 0 0 100%;
    }

    .order-text,
    .order-action {
        @media screen and (min-width: 992px) {
            flex: 1 1 auto;
        }
    }

    .order-image {
        > img {
            margin-right: auto;
            margin-left:  auto;

            width: 96px;
        }

        @media screen and (min-width: 992px) {
            flex: 0 0 96px;
        }
    }

    .order-text {
        text-align: center;

        @media screen and (min-width: 992px) {
            text-align: left;
        }
    }

    .order-text-wrapper {
        @media screen and (min-width: 992px) {
            max-width: 545px;
        }
    }

    .order-text-heading {
        font-size: 18px;
    }

    .order-button-icon {
        font-size: 16px;
    }

    .item-captions {
        flex-flow: row nowrap;

        position: sticky;
        z-index:  25;

        @media screen and (min-width: 992px) {
            top: var(--top-bar-height);
        }
    }

    .item-card {
        display:         flex;
        flex-flow:       row wrap;
        align-items:     flex-start;
        justify-content: space-between;

        @media screen and (min-width: 768px) {
            align-items: center;
            flex-flow:   row nowrap;
        }
    }

    .item-basic {
        display:     flex;
        flex:        1 1 auto;
        flex-flow:   row nowrap;
        align-items: center;
    }

    .item-basic,
    .item-caption-basic {
        padding-right: 8px;

        order:     1;
        max-width: 65%;

        @media screen and (min-width: 576px) {
            max-width: 80%;
        }

        @media screen and (min-width: 768px) {
            flex:      1 1 auto;
            max-width: none;
        }
    }

    .item-icon {
        flex:   0 0 auto;
        width:  48px;
        height: 48px;

        > img {
            border-radius: 8px;
        }

        &.user > img {
            border-radius: 50%;
        }
    }

    .item-specialization,
    .item-caption-specialization {
        padding-right: 8px;

        flex:  1 0 100%;
        order: 2;

        @media screen and (min-width: 768px) {
            flex: 0 0 250px;
        }
    }

    .item-tags {
        margin: 20px -4px -4px;

        display:   flex;
        flex-flow: row wrap;

        @media screen and (min-width: 768px) {
            margin: -4px;
        }

        > .tag {
            margin: 4px;
        }
    }

    .item-works,
    .item-caption-works {
        padding-right: 8px;

        display: none;
        order:   3;

        @media screen and (min-width: 1200px) {
            display: flex;

            flex: 0 0 311px;
        }
    }

    .item-work {
        width: 85px;
        height: 48px;
        overflow: hidden;
        border-radius: 8px;

        &+& {margin-left: 4px}

        img {
            display: block;
            object-fit: cover;
            object-position: center;
            width: 100%;
            height: 48px;
        }
    }

    .item-actions {
        margin-right: -20px;

        display:         flex;
        align-items:     center;
        justify-content: flex-end;
        flex:            0 1 auto;
        flex-flow:       row nowrap;
        order:           1;

        @media screen and (min-width: 768px) {
            margin-right: 0;
        }
    }

    .item-caption-actions {
        text-align: center;
    }

    .item-actions,
    .item-caption-actions {
        @media screen and (min-width: 768px) {
            flex:  0 0 180px;
            order: 4;
        }
    }

    .item-follow-button,
    .item-link-button {
        @media screen and (max-width: 767px) {
            font-size:  20px;
            text-align: center;

            padding: 14px 4px;

            min-width: 48px;
        }

        .icon-ic-tick-thin {
            font-size:   16px;
            line-height: 20px;

            @media screen and (min-width: 768px) {
                font-size:   12px;
                line-height: 14px;
            }
        }
    }

    .item-follow-button.following > .color-inherit {
        color: inherit !important;
    }

    .item-link-button {
        margin-left: 8px;

        @media screen and (min-width: 768px) {
            font-size: 16px;

            padding: 8px;
        }

        > .icon-ic-arrow-simple {
            transform: rotate(270deg);
        }
    }

    /* .sticky-panel {
        margin-top:    -24px;
        margin-bottom: -24px;
    } */
</style>
